import React from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';

// Utils
import { getGoogleCredentials } from 'util/credentials';
import { drawingStates } from 'constants/values';
import { zoneDrawingStateToMapDrawState } from 'util/zoneDrawing';
import { getGeometry } from 'geometry/geojson/convert';

// Actions
import { closeMessage } from 'messages';
import { setCurrentMapZoom } from 'current';
import {
  selectZone,
  removeActiveZoneSelection,
  setIsOverlayQuickSelectActive
} from 'current/uiwidgets';
import {
  setLastShapeDrawn
} from 'current/uiwidgets/drawing';
import { createStart, mergeStart, splitStart, undoZoneHistory } from 'scripts';

// Selectors
import { getBoundariesByFieldUuid } from 'boundaries/selectors';
import {
  getActiveFieldUuid,
  getActiveScript,
  getDrawingState,
  getSelectedZoneIds
} from 'current/selectors';
import {
  isSaveTriggerLocked as isSaveTriggerLockedSelector
} from 'current/uiwidgets/selectors';
import {
  canUndoZoneHistory,
  getCropForScript,
  getGeometryViolations
} from 'scripts/selectors';
import { getAllMessages } from 'messages/selectors';
import { getUnitsOfMeasureTemplate } from 'user/selectors';

// Components
import BaseMap from 'mapping/BaseMap';
import DrawingToolbar from 'mapping/DrawingToolbar';
import MessagePanel from 'messages/MessagePanel';
import EditingLayer from 'mapping/layers/EditingLayer';
import AppropriateOverlayContainer from 'mapping/AppropriateOverlayContainer';

import { lockSaveTrigger } from 'current/uiwidgets/locks';

const EditMapContainer = ({
  boundaries,
  canUndo,
  drawingState,
  hasGeometryViolations,
  isAppMode,
  messages,
  onClickCreateStart,
  onClickMergeStart,
  onClickOutsideZones,
  onClickSplitStart,
  onClickUndo,
  onClickZone,
  onCloseMessage,
  onDrawShapeComplete,
  onSetCurrentMapZoom,
  scriptId,
  selectedZoneIds,
  isSaveTriggerLocked,
  unitsOfMeasure,
  zones 
}) => {
  const _onClickUndo = () => onClickUndo(scriptId);
  const showToolbar = (drawingState === drawingStates.NONE && !hasGeometryViolations);
  const drawingToolbar = showToolbar ? (<DrawingToolbar
    canUndoZoneHistory={canUndo && drawingState === drawingStates.NONE}
    isSaveTriggerLocked={isSaveTriggerLocked}
    onClickCreateStart={onClickCreateStart}
    onClickMergeStart={onClickMergeStart}
    onClickSplitStart={onClickSplitStart}
    onClickUndo={_onClickUndo}
  />) : null;


  return (
    <div className='vra-map-panel' onClick={onClickOutsideZones}>
      <div className='vra-map-wrapper'>
        <BaseMap
          bounceAtZoomLimits={!isAppMode}
          boundaries={boundaries}
          className='vra-map'
          drawState={zoneDrawingStateToMapDrawState(drawingState)}
          googleCredentials={getGoogleCredentials()}
          isAppMode={isAppMode}
          onClick={onClickOutsideZones}
          onDrawShapeComplete={onDrawShapeComplete}
          onLeafletZoomEnd={(event) => event.target && onSetCurrentMapZoom(event.target.getZoom())}
          showScale
          unitsOfMeasure={unitsOfMeasure}>
          {
            zones && <EditingLayer
              drawingState={drawingState}
              onClickZone={onClickZone}
              selectedZoneIds={selectedZoneIds}
              zones={zones}
            />
          }
          <AppropriateOverlayContainer />
        </BaseMap>
        {drawingToolbar}
        <MessagePanel closeMessage={onCloseMessage} isToolbarShown={showToolbar} messages={messages} />
      </div>
    </div>
  );
};

EditMapContainer.propTypes = {
  boundaries: propTypes.func.isRequired,
  canUndo: propTypes.bool.isRequired,
  drawingState: propTypes.string.isRequired,
  hasGeometryViolations: propTypes.bool.isRequired,
  isAppMode: propTypes.bool.isRequired,
  isSaveTriggerLocked: propTypes.bool.isRequired,
  messages: propTypes.arrayOf(propTypes.object).isRequired,
  onClickCreateStart: propTypes.func.isRequired,
  onClickMergeStart: propTypes.func.isRequired,
  onClickOutsideZones: propTypes.func.isRequired,
  onClickSplitStart: propTypes.func.isRequired,
  onClickUndo: propTypes.func.isRequired,
  onClickZone: propTypes.func.isRequired,
  onCloseMessage: propTypes.func.isRequired,
  onDrawShapeComplete: propTypes.func.isRequired,
  onSetCurrentMapZoom: propTypes.func.isRequired,
  scriptId: propTypes.string,
  selectedZoneIds: propTypes.arrayOf(propTypes.string).isRequired,
  unitsOfMeasure: propTypes.string,
  zones: propTypes.object.isRequired
};

export default connect(
  (state) => {
    const fieldUuid = getActiveFieldUuid(state);
    const script = getActiveScript(state);
    return {
      boundaries: getBoundariesByFieldUuid(state, fieldUuid),
      canUndo: canUndoZoneHistory(script),
      crop: script && getCropForScript(script),
      drawingState: getDrawingState(state),
      hasGeometryViolations: !!getGeometryViolations(script),
      isAppMode: state.settings.isAppMode,
      isSaveTriggerLocked: isSaveTriggerLockedSelector(state, script),
      messages: getAllMessages(state),
      scriptId: script && script.id,
      selectedZoneIds: getSelectedZoneIds(state),
      zones: script && script.zones,
      unitsOfMeasure: getUnitsOfMeasureTemplate(state)
    };
  },
  (dispatch) => {
    const _isZoneLayer = (el) => el.nodeName.toLowerCase() === 'path';
    return {
      onCloseMessage: (id) => { dispatch(closeMessage(id)); },
      onSetCurrentMapZoom: (zoom) => { dispatch(setCurrentMapZoom(zoom)); },
      onClickCreateStart: (shapeType) => {
        dispatch(lockSaveTrigger());
        dispatch(createStart(shapeType));
      },
      onClickMergeStart: () => {
        dispatch(lockSaveTrigger());
        dispatch(mergeStart());
      },
      onClickSplitStart: () => {
        dispatch(lockSaveTrigger());
        dispatch(splitStart());
      },
      onClickUndo: (scriptId) => { dispatch(undoZoneHistory(scriptId)); },
      onDrawShapeComplete: (shapeGeometry) => {
        dispatch(setLastShapeDrawn(getGeometry(shapeGeometry)));
      },
      onClickZone: (zoneId) => { dispatch(selectZone({ mapWasClickedOnZoneSelect: true, zoneId })); },
      onClickOutsideZones: (e) => {
        dispatch(setIsOverlayQuickSelectActive(false));
        if (!_isZoneLayer(e.target)) {
          dispatch(removeActiveZoneSelection());
        }
      }
    };
  }
)(EditMapContainer);
